import { useApi, API_ENDPOINTS, transformPascalToCamel } from "@/lib/api"
import type { MappingConfig, AtsDestinationFieldInfo } from "@/types/fbo-api"

export function useMappingConfig() {
  const { mainApi } = useApi()

  const loadMappingNames = async (): Promise<string[]> => {
    const names = await mainApi.get<string[]>(API_ENDPOINTS.MAIN.ATS_FIELD_MAPPING_NAMES)
    return names
  }

  // Field-mapping configs are stored per ATS source. atsSource scopes the read/write to
  // the source the user has selected (the source switcher); omitting it lets the server
  // fall back to the tenant's active source.
  const mappingsUrl = (mappingName: string, atsSource?: string | null) => {
    const base = API_ENDPOINTS.MAIN.ATS_FIELD_MAPPINGS(mappingName)
    return atsSource ? `${base}?atsSource=${encodeURIComponent(atsSource)}` : base
  }

  const loadConfig = async (mappingName: string, atsSource?: string | null): Promise<MappingConfig> => {
    // Use real API call
    const response = await mainApi.get<any>(mappingsUrl(mappingName, atsSource))
    // Transform from Pascal case (API) to camel case (internal)
    return transformPascalToCamel(response) as MappingConfig
  }

  const loadAvailableDestinationFields = async (mappingName: string): Promise<AtsDestinationFieldInfo[]> => {
    const fieldInfos = await mainApi.get<AtsDestinationFieldInfo[]>(API_ENDPOINTS.MAIN.ATS_FIELD_MAPPING_DESTINATION_FIELDS(mappingName))
    return fieldInfos
  }

  const saveConfig = async (mappingName: string, config: MappingConfig, atsSource?: string | null): Promise<boolean> => {
    try {
      // Send config directly - API should handle the format
      await mainApi.post(mappingsUrl(mappingName, atsSource), config)
      return true
    } catch (err) {
      console.error('Failed to save configuration:', err)
      return false
    }
  }

  return {
    loadMappingNames,
    loadConfig,
    loadAvailableDestinationFields,
    saveConfig,
  }
} 